import { FC, useMemo, useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import cx from "classnames"
import EmptyList from "@/components/EmptyList"
import AddIcon from "@/components/icons/AddIcon"
import OrganizationCard from "../../OrganizationCard"
import ErrorMessage from "@/components/ErrorMessage"
import Button from "@/components/Button"
import AdminAddOrganizationModal from "../AdminAddOrganizationModal"
import AdminEditOrganizationModal from "../AdminEditOrganizationModal"
import AdminSetOrganizationStatusModal from "../AdminSetOrganizationStatusModal"
import AdminDeleteOrganizationModal from "../AdminDeleteOrganizationModal"
import Loader from "@/components/Loader"
import { alphabetDateSortBy } from "../../../utils"
import { useAdminOrganizationsInfiniteScroll } from "./useAdminOrganizationsInfiniteScroll"
import type { AdminOrganization } from "@/types/entities/organization"
import { useOrganizationPermissions } from "@/hooks/useOrganizationPermissions"

type OrganizationModal = null | "edit" | "delete" | "set status"

const AdminOrganizationsList: FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isActive = searchParams.get("isActive")
    ? searchParams.get("isActive") === "true"
    : "true"
  const owner = searchParams.get("owner")
  const {
    paginatedOrgs: orgs,
    isLoading,
    isFetching,
    isError,
    updateOrganization,
    updateOrganizationStatus,
    deleteOrganization,
    sentryRef,
    refetchOrganizations,
  } = useAdminOrganizationsInfiniteScroll()
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    null | string
  >(null)
  const [openedModal, setOpenedModal] = useState<OrganizationModal>(null)
  const [selectedOrgData, setSelectedOrgData] =
    useState<AdminOrganization | null>(null)
  const { accessiblePages } = useOrganizationPermissions()

  // Auto-redirect to the only organization if admin has only one
  useEffect(() => {
    // Only redirect if data has loaded and admin has exactly one organization that's active
    if (!isLoading && orgs.length === 1 && orgs[0].status === "enabled") {
      const org = orgs[0]

      // Prioritize dashboard and inbox, then fallback to other accessible pages
      let redirectPage = "dashboard"

      // First check if dashboard is accessible
      if (accessiblePages.includes("/dashboard")) {
        redirectPage = "dashboard"
      }
      // Then check if inbox is accessible
      else if (accessiblePages.includes("/inbox")) {
        redirectPage = "inbox"
      }
      // Otherwise use the first accessible page
      else {
        const firstAccessiblePage = accessiblePages
          .find((page) => page)
          ?.replace(/^\//, "")
        if (firstAccessiblePage) {
          redirectPage = firstAccessiblePage
        }
      }

      // Navigate to the organization's page
      navigate(`/organization/${org._id}/${redirectPage}`)
    }
  }, [orgs, isLoading, navigate, accessiblePages])

  const emptyMessage = isActive
    ? `There is no active organization${owner ? " for this owner" : ""}.`
    : `There is no inactive organization${owner ? " for this owner" : ""}.`
  const [openAddModal, setOpenAddModal] = useState(false)
  const [deletedId, setDeletedId] = useState("")

  const handleOpenAddModal = () => setOpenAddModal(true)
  const closeAddModal = () => setOpenAddModal(false)

  const handleUpdateOrganization = (body: AdminOrganization) => {
    // First update the local state
    updateOrganization({ body, id: body._id })

    // Update our selected org data to ensure it has the latest values
    setSelectedOrgData(body)

    // Force a refetch to ensure we have the latest data including permission fields
    setTimeout(() => {
      refetchOrganizations()
    }, 500) // Small delay to ensure the update has completed
  }
  const handleUpdateOrganizationStatus = (payload: AdminOrganization) =>
    updateOrganizationStatus({ body: payload.status, id: payload._id })

  const handleCardClick = (id: string) => {
    return () => {
      // Define routes in order of priority
      const priorityRoutes = [
        {
          path: "/dashboard",
          route: `/organization/${id}/dashboard?indicators=all`,
        },
        { path: "/campaigns", route: `/organization/${id}/campaigns` },
        { path: "/prospects", route: `/organization/${id}/prospects` },
        { path: "/dnc", route: `/organization/${id}/dnc` },
        { path: "/inbox", route: `/organization/${id}/inbox` },
        { path: "/sequences", route: `/organization/${id}/sequences` },
        { path: "/master-lists", route: `/organization/${id}/master-lists` },
        {
          path: "/search-criteria",
          route: `/organization/${id}/search-criteria`,
        },
        { path: "/domains", route: `/organization/${id}/domains` },
        { path: "/emails", route: `/organization/${id}/emails` },
        { path: "/senders", route: `/organization/${id}/senders` },
      ]

      // For admin users, always navigate to dashboard
      if (accessiblePages?.includes("/dashboard")) {
        navigate(`/organization/${id}/dashboard?indicators=all`)
        return
      }

      // For non-admin users or if dashboard isn't accessible, find the first accessible page
      const firstAccessible = priorityRoutes.find((route) => {
        const isAccessible = accessiblePages?.some(
          (page) => page === route.path,
        )
        return isAccessible
      })

      if (firstAccessible) {
        navigate(firstAccessible.route)
      } else {
        // If no accessible pages are found, navigate to organizations list
        console.warn("No accessible pages found for this organization")
        navigate("/organizations")
      }
    }
  }

  const orgsFiltered = useMemo<AdminOrganization[]>(() => {
    if (!orgs.length) {
      return []
    }
    const searchedOrgsTerm = searchParams.get("term")
    let resultOrgs = []
    resultOrgs = isActive
      ? orgs.filter(({ status }) => status === "enabled")
      : orgs.filter(({ status }) => status === "disabled")
    if (searchedOrgsTerm) {
      resultOrgs = resultOrgs.filter(({ name }) =>
        name.trim().toLowerCase().includes(searchedOrgsTerm.toLowerCase()),
      )
    }
    const sortBy = searchParams.get("sortBy") ?? "Newest"
    if (sortBy) {
      resultOrgs = alphabetDateSortBy(sortBy, resultOrgs) as AdminOrganization[]
    }
    if (owner) {
      return resultOrgs.filter(({ owner: { _id } }) => {
        return _id === owner
      })
    }
    return resultOrgs
  }, [isActive, orgs, searchParams, owner])

  const openOrganizationModal = (id: string) => {
    setSelectedOrganizationId(id)
  }

  const onOrganizationModalOpen = (
    id: string,
    modalType: OrganizationModal,
  ) => {
    return () => {
      openOrganizationModal(id)
      setOpenedModal(modalType)
    }
  }

  const handleEditModal = (id: string) => {
    // First refetch organizations to ensure we have the latest data
    refetchOrganizations()

    // Find the organization and store it in state
    const foundOrg = orgs.find(({ _id }) => _id === id)
    if (foundOrg) {
      // Ensure permission fields are properly initialized
      const safeOrg = {
        ...foundOrg,
        fullAccessUsers: Array.isArray(foundOrg.fullAccessUsers)
          ? foundOrg.fullAccessUsers
          : [],
        editAccessUsers: Array.isArray(foundOrg.editAccessUsers)
          ? foundOrg.editAccessUsers
          : [],
        editAccessPages: Array.isArray(foundOrg.editAccessPages)
          ? foundOrg.editAccessPages
          : [],
        readAccessUsers: Array.isArray(foundOrg.readAccessUsers)
          ? foundOrg.readAccessUsers
          : [],
        readAccessPages: Array.isArray(foundOrg.readAccessPages)
          ? foundOrg.readAccessPages
          : [],
        specialPermissions: foundOrg.specialPermissions || {
          dncListEdit: false,
        },
      }
      setSelectedOrgData(safeOrg)
    } else {
      console.error("Organization not found in list:", id)
    }

    setSelectedOrganizationId(id)
    setOpenedModal("edit")
  }

  const selectedOrganization = useMemo(
    () => orgs.find(({ _id }) => _id === selectedOrganizationId) || ({} as any),
    [orgs, selectedOrganizationId],
  )

  const closeModal = () => {
    setSelectedOrganizationId(null)
    setOpenedModal(null)
    setSelectedOrgData(null)
  }

  const editModalOpened =
    selectedOrganizationId && selectedOrgData && openedModal === "edit"
  const setStatusModalOpened =
    selectedOrganization &&
    selectedOrganizationId &&
    openedModal === "set status"

  if (isError) {
    return <ErrorMessage />
  }

  return (
    <>
      <div
        className={cx("grid gap-5 mt-4 flex-1", {
          "grid-cols-1": !orgsFiltered.length,
          "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4": orgsFiltered.length,
          "place-items-center min-h-[300px]": !orgs.length,
        })}
      >
        {isLoading ? (
          <Loader />
        ) : orgsFiltered.length ? (
          <>
            {orgsFiltered.map(({ name, status, _id, owner, isMaster }) => (
              <OrganizationCard
                key={_id}
                name={name}
                enabled={status === "enabled"}
                isMaster={isMaster}
                footerName={`${owner?.firstName} ${owner?.lastName}`}
                onEdit={() => handleEditModal(_id)}
                onDelete={() => setDeletedId(_id)}
                onChangeStatus={onOrganizationModalOpen(_id, "set status")}
                onClick={handleCardClick(_id)}
              />
            ))}
          </>
        ) : (
          <EmptyList message={emptyMessage} className="mt-[88px]">
            {isActive && (
              <Button
                onClick={handleOpenAddModal}
                className="btn-sm group bg-white hover:bg-primary border !border-solid border-outline-dark hover:border-primary"
              >
                <AddIcon className="transition-colors text-primary group-hover:text-white" />
                <span className="transition-colors font-medium text-primary group-hover:text-white">
                  Add New Organization
                </span>
              </Button>
            )}
          </EmptyList>
        )}
      </div>
      {isFetching && !isLoading && <Loader className="mx-auto mt-4" />}
      <div ref={sentryRef} className="mb-20"></div>
      <AdminAddOrganizationModal open={openAddModal} onClose={closeAddModal} />
      {editModalOpened && (
        <AdminEditOrganizationModal
          data={selectedOrgData}
          open={!!selectedOrganizationId}
          onClose={closeModal}
          onEditSuccess={handleUpdateOrganization}
        />
      )}
      {setStatusModalOpened && (
        <AdminSetOrganizationStatusModal
          currentStatus={selectedOrganization.status}
          organizationId={selectedOrganizationId}
          initialName={selectedOrganization.name}
          open={!!selectedOrganizationId}
          onClose={closeModal}
          onUpdateStatusSuccess={handleUpdateOrganizationStatus}
        />
      )}
      <AdminDeleteOrganizationModal
        data={orgs.find(({ _id }) => _id === deletedId)}
        open={!!deletedId}
        onClose={() => setDeletedId("")}
        onDeleteSuccess={deleteOrganization}
      />
    </>
  )
}

export default AdminOrganizationsList
